import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { useApp } from '../../context/AppContext';
import { fetchDepositAddresses, createDeposit, fetchUserDeposits } from '../../api';
import { toast } from 'sonner';
import { X, Wallet, Copy, Check, ArrowRight, ArrowLeft, Clock, ShieldCheck, Bitcoin } from 'lucide-react';

const COINS = [
  { coin: 'USDT', networks: ['ERC20', 'BEP20'], color: '#26a17b' },
  { coin: 'USDC', networks: ['ERC20', 'BEP20'], color: '#2775ca' },
  { coin: 'BTC', networks: ['BTC'], color: '#f7931a' },
];

const qrSrc = (addr, custom) =>
  custom && custom.length > 10 ? custom : `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=131316&color=ffffff&qzone=2&data=${encodeURIComponent(addr)}`;

const statusStyle = {
  pending: 'text-[var(--orange)] bg-[#2a1a0e] border-[#4a2f14]',
  verified: 'text-[var(--green)] bg-[#0e2a20] border-[#1e5b45]',
  rejected: 'text-[var(--red)] bg-[#2a1010] border-[#5a2020]',
};

export default function VaultModal({ open, onClose }) {
  const { vaultBalance, user } = useApp();
  const [addresses, setAddresses] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [coin, setCoin] = useState('USDT');
  const [network, setNetwork] = useState('ERC20');
  const [amount, setAmount] = useState('');
  const [txHash, setTxHash] = useState('');
  const [step, setStep] = useState(1); // 1 select, 2 pay, 3 done
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadDeposits = useCallback(async () => {
    if (user) setDeposits(await fetchUserDeposits(user.id).catch(() => []));
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchDepositAddresses().then(setAddresses).catch(() => {});
      loadDeposits();
      setStep(1); setAmount(''); setTxHash(''); setCoin('USDT'); setNetwork('ERC20');
    }
  }, [open, loadDeposits]);

  const activeCoin = COINS.find((c) => c.coin === coin);
  const addr = addresses.find((a) => a.coin === coin && a.network === network);

  const selectCoin = (c) => {
    setCoin(c.coin);
    setNetwork(c.networks[0]);
  };

  const goToPay = () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid deposit amount'); return; }
    if (!addr) { toast.error('Deposit address unavailable for this network'); return; }
    setStep(2);
  };

  const copyAddr = () => {
    navigator.clipboard?.writeText(addr?.address || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const submitPaid = async () => {
    if (!txHash || txHash.length < 6) { toast.error('Enter the transaction hash of your payment'); return; }
    setBusy(true);
    try {
      await createDeposit(user.id, { coin, network, amount: parseFloat(amount), txHash });
      setBusy(false);
      setStep(3);
      loadDeposits();
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || 'Submission failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border)] text-[var(--text)] max-w-md p-0 gap-0 [&>button]:hidden max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2.5">
            <Wallet size={18} className="text-[var(--orange)]" />
            <h3 className="font-display font-semibold text-white text-[15px]">Institutional Crypto Vault</h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-white p-1"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="p-4 rounded-lg bg-[var(--bg)] border border-[var(--border)] flex items-center justify-between">
            <span className="text-[11px] font-mono tracking-widest text-[var(--muted-2)]">VAULT BALANCE</span>
            <span className="font-mono text-[18px] text-[var(--green)]">${vaultBalance.toLocaleString()}</span>
          </div>

          {step === 1 && (
            <>
              <div>
                <div className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] mb-2">SELECT ASSET</div>
                <div className="grid grid-cols-3 gap-2">
                  {COINS.map((c) => (
                    <button key={c.coin} onClick={() => selectCoin(c)} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-[13px] font-semibold transition-colors ${coin === c.coin ? 'border-[var(--orange)] bg-[#2a1a0e] text-white' : 'border-[var(--border)] text-[var(--muted)] hover:text-white'}`}>
                      {c.coin === 'BTC' && <Bitcoin size={14} style={{ color: c.color }} />}
                      {c.coin}
                    </button>
                  ))}
                </div>
              </div>

              {activeCoin.networks.length > 1 && (
                <div>
                  <div className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] mb-2">NETWORK</div>
                  <div className="grid grid-cols-2 gap-2">
                    {activeCoin.networks.map((n) => (
                      <button key={n} onClick={() => setNetwork(n)} className={`py-2.5 rounded-lg border text-[12.5px] font-medium transition-colors ${network === n ? 'border-[var(--orange)] bg-[#2a1a0e] text-white' : 'border-[var(--border)] text-[var(--muted)] hover:text-white'}`}>
                        {n} <span className="text-[var(--muted-2)]">({coin} · {n === 'ERC20' ? 'Ethereum' : 'BNB Chain'})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">DEPOSIT AMOUNT (USD)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50000" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 font-mono text-[16px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors" />
                <div className="flex gap-2 mt-2">
                  {[25000, 50000, 100000, 250000].map((p) => (
                    <button key={p} onClick={() => setAmount(String(p))} className="flex-1 py-1.5 rounded-md border border-[var(--border)] text-[11px] font-mono text-[var(--muted)] hover:text-white hover:border-[#3a3a42] transition-colors">${p / 1000}K</button>
                  ))}
                </div>
              </div>

              <button onClick={goToPay} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors">
                CONTINUE TO PAYMENT <ArrowRight size={16} />
              </button>
            </>
          )}

          {step === 2 && addr && (
            <>
              <div className="text-center">
                <div className="text-[12px] text-[var(--muted)]">Send exactly the equivalent of <span className="text-white font-semibold font-mono">${parseFloat(amount).toLocaleString()}</span> in</div>
                <div className="text-[15px] font-semibold text-white mt-1">{coin} <span className="text-[var(--orange)]">· {network}</span></div>
              </div>
              <div className="flex justify-center">
                <div className="p-3 bg-white rounded-xl">
                  <img src={qrSrc(addr.address, addr.qr)} alt="deposit QR" width={180} height={180} className="rounded" />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] mb-1.5">{coin} ({network}) DEPOSIT ADDRESS</div>
                <div className="flex items-center gap-2 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5">
                  <span className="font-mono text-[12px] text-white break-all flex-1">{addr.address}</span>
                  <button onClick={copyAddr} className="text-[var(--orange)] hover:text-[var(--orange-bright)] shrink-0">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-[#2a1a0e] border border-[#4a2f14] text-[11.5px] text-[#f0c99a] leading-relaxed">
                Send only <b className="text-white">{coin}</b> over the <b className="text-white">{network}</b> network to this address. After paying, paste your transaction hash below and click "I HAVE PAID".
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">TRANSACTION HASH (TXID)</label>
                <input value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="0x... / tx id" className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 font-mono text-[13px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors" />
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setStep(1)} className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[var(--border)] text-[var(--muted)] text-[13px] hover:text-white transition-colors"><ArrowLeft size={15} /> Back</button>
                <button onClick={submitPaid} disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg bg-[var(--green)] text-[#052e20] font-semibold text-[14px] hover:bg-[#13b878] transition-colors disabled:opacity-60">
                  <Check size={16} /> {busy ? 'SUBMITTING…' : 'I HAVE PAID'}
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-[#0e2a20] border border-[#1e5b45] flex items-center justify-center mx-auto">
                <Clock size={26} className="text-[var(--green)]" />
              </div>
              <h4 className="font-display font-bold text-[18px] text-white mt-4">Payment Submitted</h4>
              <p className="text-[13px] text-[var(--muted)] mt-2">Your deposit is pending verification by our treasury desk. Once confirmed on-chain, <span className="text-white">${parseFloat(amount).toLocaleString()}</span> will be credited to your vault and you'll be notified by email.</p>
              <button onClick={() => setStep(1)} className="mt-5 px-6 py-2.5 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] text-white text-[13px] hover:border-[#3a3a42] transition-colors">Make another deposit</button>
            </div>
          )}

          {/* Recent deposits */}
          {deposits.length > 0 && step !== 2 && (
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[var(--muted-2)] mb-2 pt-1 border-t border-[var(--border)] pt-3">RECENT DEPOSITS</div>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                {deposits.slice(0, 6).map((d) => (
                  <div key={d.id} className="flex items-center justify-between bg-[var(--bg)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-[12px]">
                    <div>
                      <span className="text-white font-mono">${d.amount.toLocaleString()}</span>
                      <span className="text-[var(--muted-2)]"> · {d.coin} {d.network}</span>
                    </div>
                    <span className={`text-[9px] font-mono px-2 py-0.5 rounded border ${statusStyle[d.status] || ''}`}>{d.status.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-[10px] font-mono text-[var(--muted-2)]">SIMULATED CRYPTO ESCROW · ADMIN-VERIFIED SETTLEMENT</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
